import { addDays, differenceInDays } from 'date-fns';
import { injectable } from 'inversify';

import EpgService from '../services/EpgService';
import { EPG_TIMELINE_DAYS_AFTER, EPG_TIMELINE_DAYS_BEFORE, EPG_TYPE } from '../constants';
import { getNamedModule } from '../modules/container';
import type { PlaylistItem } from '../../types/playlist';
import type { EpgChannel, EpgProgram, EpgProgramNavigation } from '../../types/epg';
import { logError, logWarn } from '../logger';

const isFulfilled = <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> => {
  if (input.status === 'fulfilled') {
    return true;
  }

  logError('EpgController', `An error occurred resolving a promise: `, { error: input.reason });
  return false;
};

@injectable()
export default class EpgController {
  /**
   * Update the start and end time properties of the given programs with the current date.
   * This can be used when having a static schedule or while developing
   */
  private generateDemoPrograms(programs: EpgProgram[]) {
    const today = new Date();
    const startDate = new Date(programs[0]?.startTime);

    // this makes sure that the start of the day is correct. `startOfDay(startDate)` doesn't work since it can yield
    // a different date depending on the timezone.
    // for example, given a startTime of `2022-08-03T23:00:00Z` will parse to `2022-08-04T01:00:00+0200` in
    // Europe/Amsterdam (GMT+2) which makes startOfDay return `2022-08-04T00:00:00+0200`.
    const utcStartDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()));
    const daysDelta = differenceInDays(today, utcStartDate);

    return programs.map((program, idx) => ({
      ...program,
      id: `${program.id}_${idx}`,
      startTime: addDays(new Date(program.startTime), daysDelta).toJSON(),
      endTime: addDays(new Date(program.endTime), daysDelta).toJSON(),
    }));
  }

  parsePrograms = (programs: EpgProgram[], channel: EpgChannel) => {
    return channel.demo ? this.generateDemoPrograms(programs) : programs;
  };

  getChannels = async (items: PlaylistItem[], startTime?: number, endTime?: number): Promise<EpgChannel[]> => {
    const epgService = this.getEpgService(items[0]);

    if (!epgService) throw new Error('EPG service not configured properly');

    const channels = await epgService.fetchChannels(items, startTime, endTime);
    const programs = await epgService.fetchPrograms(items, startTime, endTime);

    return channels.map((channel) => {
      const channelPrograms = programs.filter((program) => program.channelId === channel.id);

      return {
        ...channel,
        programs: this.parsePrograms(channelPrograms, channel),
      } as EpgChannel;
    });
  };

  getEpgService = (item?: Pick<PlaylistItem, 'scheduleType'>) => {
    const scheduleType = item?.scheduleType?.toLocaleLowerCase() || EPG_TYPE.jwp;
    const service = getNamedModule(EpgService, scheduleType, false);

    if (!service) {
      logWarn('EpgController', `No epg service was added for the ${scheduleType} schedule type`);
    }

    return service;
  };

  getProgramNavigation = (channelItem: PlaylistItem, program: EpgProgram, channel: EpgChannel): EpgProgramNavigation | undefined => {
    const epgService = this.getEpgService(channelItem);

    return epgService?.getProgramNavigation(program, channel);
  };

  getEpgDays = (items: PlaylistItem[]) => {
    const scheduleTypes = new Set(items.map((item) => item.scheduleType?.toLocaleLowerCase() || EPG_TYPE.jwp));
    const days = [...scheduleTypes].map((scheduleType) => this.getEpgService({ scheduleType })?.epgDays ?? 1);

    return Math.min(Math.max(...days, 1), EPG_TIMELINE_DAYS_BEFORE, EPG_TIMELINE_DAYS_AFTER);
  };

  /**
   * Get all schedules for the given PlaylistItem's
   */
  getSchedules = async (items: PlaylistItem[], startTime?: number, endTime?: number) => {
    // group all items per scheduleType (provider)
    const grouped = items.reduce<Record<string, PlaylistItem[]>>((acc, item) => {
      const scheduleType = item.scheduleType || EPG_TYPE.jwp;
      acc[scheduleType] ??= [];
      acc[scheduleType].push(item);
      return acc;
    }, {});

    // get all channels for each schedule type; a failing group (e.g. a missing service) drops its channels
    const settled = await Promise.allSettled(Object.values(grouped).map((group) => this.getChannels(group, startTime, endTime)));
    const results = settled.filter(isFulfilled).map((result) => result.value);

    // create a dictionary with the media id so we can sort the channels based on the original `items` order
    const referenceDictionary = Object.fromEntries(results.flat().map((channel) => [channel.id, channel]));

    return items.map((item) => referenceDictionary[item.mediaid]).filter((item) => !!item);
  };
}
